import { useState } from 'react'
import type { ProgramItemWithVideo } from '../../services/programService'
import { VideoPlayerModal } from '../video'

interface ProgramExercisesTabProps {
  items: ProgramItemWithVideo[]
}

export function ProgramExercisesTab({ items }: ProgramExercisesTabProps) {
  const [selectedVideo, setSelectedVideo] = useState<ProgramItemWithVideo['video'] | null>(null)

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getThumbnailUrl = (video: ProgramItemWithVideo['video']): string | null => {
    if (video.thumbnail_url) return video.thumbnail_url
    if (video.mux_playback_id) {
      const time = video.thumbnail_time || 0
      return `https://image.mux.com/${video.mux_playback_id}/thumbnail.png?width=320&height=180&time=${time}&fit_mode=smartcrop`
    }
    return null
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
        <h3 className="mt-3 text-lg font-medium text-gray-900">No exercises yet</h3>
        <p className="mt-1 text-sm text-gray-500">
          Edit this program to add exercises.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const thumbnailUrl = getThumbnailUrl(item.video)

        return (
          <div
            key={item.id}
            className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors"
          >
            {/* Position indicator */}
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                {index + 1}
              </span>
            </div>

            {/* Thumbnail */}
            <button
              onClick={() => setSelectedVideo(item.video)}
              className="relative flex-shrink-0 w-32 h-20 bg-gray-200 rounded-lg overflow-hidden group cursor-pointer"
            >
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={item.video.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
              {/* Play overlay on hover */}
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              {/* Duration badge */}
              <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-xs text-white">
                {formatDuration(item.video.duration_seconds)}
              </div>
            </button>

            {/* Exercise details */}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-gray-900 truncate">
                {item.video.title}
              </h4>
              {item.notes && (
                <p className="mt-0.5 text-sm text-gray-500 truncate">
                  {item.notes}
                </p>
              )}
              {/* Exercise parameters */}
              {(item.sets || item.reps || item.duration_seconds) && (
                <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                  {item.sets && (
                    <span className="flex items-center gap-1">
                      <span className="font-medium">{item.sets}</span> sets
                    </span>
                  )}
                  {item.reps && (
                    <span className="flex items-center gap-1">
                      <span className="font-medium">{item.reps}</span> reps
                    </span>
                  )}
                  {item.duration_seconds && (
                    <span className="flex items-center gap-1">
                      <span className="font-medium">
                        {formatDuration(item.duration_seconds)}
                      </span>{' '}
                      duration
                    </span>
                  )}
                  {item.rest_seconds && (
                    <span className="flex items-center gap-1">
                      <span className="font-medium">
                        {formatDuration(item.rest_seconds)}
                      </span>{' '}
                      rest
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Status indicator */}
            {item.video.status !== 'ready' && (
              <span className="flex-shrink-0 px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800">
                Processing
              </span>
            )}
          </div>
        )
      })}

      {/* Video preview modal */}
      <VideoPlayerModal
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        video={selectedVideo}
      />
    </div>
  )
}
